import { useEffect } from 'react';
import { Flex, Skeleton } from 'antd';
import { useStore } from '../../../helpers/use-store';
import { useNotification } from '../../../hooks/use-notification';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

declare global {
  interface Window {
    onTikTokAuthSuccess?: (data: { code: string; state: string }) => void;
    onTikTokAuthError?: (error: string) => void;
  }
}

export default function TikTokCallback() {
  const {
    dataStore: { socialAccountStore },
  } = useStore();
  const notificationApi = useNotification();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const onSuccess = async (data: { code: string; state: string }) => {
    try {
      const response = await socialAccountStore.tiktokAuth(data);

      if (response) {
        notificationApi.success({
          placement: 'top',
          icon: '',
          message: t('common.success'),
          description: t('socialAccounts.tiktok.addedSuccess'),
        });
      } else {
        onError();
      }
    } catch (e) {
      console.error(`failed tiktok auth`, e);
    }
  };

  const onError = () => {
    notificationApi.error({
      placement: 'top',
      icon: '',
      message: t('common.error'),
      description: t('socialAccounts.tiktok.addError'),
    });
  };

  useEffect(() => {
    const handleAuth = async () => {
      const params = new URLSearchParams(window.location.search);
      const code = params.get('code');
      const state = params.get('state') || '';
      const error = params.get('error');
      const errorDescription = params.get('error_description');

      // Define popup success/error functions
      const popupSuccess = (data: { code: string; state: string }) => {
        window.opener.onTikTokAuthSuccess?.(data);
        window.close();
      };

      const popupError = (error: string) => {
        window.opener.onTikTokAuthError?.(error);
        window.close();
      };

      // Define redirect success/error functions
      const redirectSuccess = async (data: { code: string; state: string }) => {
        await onSuccess(data);
        navigate('/videos');
      };

      const redirectError = () => {
        onError();
        navigate('/videos');
      };

      // Assign final handlers based on window.opener
      const handleSuccess = window.opener ? popupSuccess : redirectSuccess;
      const handleError = window.opener ? popupError : redirectError;

      try {
        if (error || !code) {
          handleError(errorDescription || 'Authentication failed');
        } else {
          await handleSuccess({ code, state });
        }
      } catch (error) {
        handleError('Failed to connect TikTok account');
      }
    };

    handleAuth();
  }, []);

  return (
    <Flex
      style={{ width: '100%', height: '100%', justifyContent: 'center' }}
      align={'center'}
    >
      <Skeleton
        style={{ height: '100%' }}
        paragraph={{ rows: 10 }}
        active={true}
      />
    </Flex>
  );
}
